import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import type { ProfileResponse, ProfileUpdateInput, UserPublic } from '@masha/shared';
import { api, ApiError } from '../lib/api';
import { setBackButton, hideBackButton } from '../lib/tma';
import { Button, ProgressBar, Textarea, Toast } from '../components/ui';

type StepKey = 'niche' | 'product' | 'targetAudience';

interface Step {
  key: StepKey;
  title: string;
  hint: string;
  placeholder: string;
}

const STEPS: readonly Step[] = [
  {
    key: 'niche',
    title: 'Опиши свою нишу: в чём ты эксперт?',
    hint: 'Чем точнее опишешь — тем сильнее будут сценарии.',
    placeholder: 'Например: психология отношений, нутрициология, бьюти-коучинг',
  },
  {
    key: 'product',
    title: 'Опиши свой продукт',
    hint: 'Что именно ты продаёшь — услугу, курс, консультацию?',
    placeholder: 'Например: курс «Восстановление после развода», 8 недель, групповой',
  },
  {
    key: 'targetAudience',
    title: 'Опиши свою аудиторию',
    hint: 'Кто эти люди, что у них болит, чего хотят.',
    placeholder: 'Например: женщины 30–45, в браке, с детьми, эмоциональное выгорание',
  },
] as const;

const stepFormSchema = z.object({
  value: z.string().trim().min(1, 'Поле не может быть пустым').max(500, 'Не больше 500 символов'),
});
type StepFormValues = z.infer<typeof stepFormSchema>;

function detectInitialStep(user: UserPublic): number {
  if (!user.niche) return 1;
  if (!user.product) return 2;
  if (!user.targetAudience) return 3;
  return 1; // unreachable: RedirectByOnboarding не пустит сюда онбордившихся
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data } = useQuery<ProfileResponse>({ queryKey: ['profile'] });
  const user = data!.user;

  const initialStep = useMemo(() => detectInitialStep(user), [user]);
  const [currentStep, setCurrentStep] = useState<number>(initialStep);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // currentStep всегда в диапазоне 1..STEPS.length — non-null assertion безопасен
  const step = STEPS[currentStep - 1]!;

  const mutation = useMutation({
    mutationFn: (patch: ProfileUpdateInput) => api.updateProfile(patch),
    onSuccess: (resp) => {
      queryClient.setQueryData(['profile'], resp);
      setSubmitError(null);
      if (resp.user.onboardedAt !== null) {
        navigate('/', { replace: true });
        return;
      }
      setCurrentStep((s) => Math.min(s + 1, STEPS.length));
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Не удалось сохранить, попробуй ещё раз';
      setSubmitError(msg);
    },
  });

  useEffect(() => {
    if (currentStep > 1) {
      return setBackButton(() => {
        setSubmitError(null);
        setCurrentStep((s) => Math.max(s - 1, 1));
      });
    }
    hideBackButton();
    return undefined;
  }, [currentStep]);

  const defaultValue = (user[step.key] ?? '') as string;
  const progressPct = Math.round((currentStep / STEPS.length) * 100);

  return (
    <div className="min-h-screen p-6 max-w-lg mx-auto safe-top safe-bottom">
      <header className="mb-6">
        <div className="flex items-center justify-between text-xs text-tg-hint mb-2">
          <span className="font-medium tabular-nums">
            Шаг {currentStep} из {STEPS.length} · {progressPct}%
          </span>
        </div>
        <ProgressBar
          value={progressPct}
          ariaLabel={`Прогресс онбординга: шаг ${currentStep} из ${STEPS.length}`}
          className="mb-4"
        />
        <h1 className="text-xl font-bold mb-1.5 leading-snug">{step.title}</h1>
        <p className="text-sm text-tg-hint">{step.hint}</p>
      </header>

      <StepForm
        key={currentStep}
        defaultValue={defaultValue}
        placeholder={step.placeholder}
        isLast={currentStep === STEPS.length}
        isSubmitting={mutation.isPending}
        submitError={submitError}
        onSubmit={(value) => {
          mutation.mutate({ [step.key]: value } as ProfileUpdateInput);
        }}
      />
    </div>
  );
}

interface StepFormProps {
  defaultValue: string;
  placeholder: string;
  isLast: boolean;
  isSubmitting: boolean;
  submitError: string | null;
  onSubmit: (value: string) => void;
}

function StepForm({
  defaultValue,
  placeholder,
  isLast,
  isSubmitting,
  submitError,
  onSubmit,
}: StepFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<StepFormValues>({
    defaultValues: { value: defaultValue },
    resolver: zodResolver(stepFormSchema),
  });

  return (
    <form onSubmit={handleSubmit((v) => onSubmit(v.value.trim()))} className="space-y-4">
      <Textarea
        {...register('value')}
        placeholder={placeholder}
        rows={5}
        invalid={!!errors.value}
      />
      {errors.value && <Toast tone="destructive">{errors.value.message}</Toast>}
      <Button
        type="submit"
        disabled={isSubmitting}
        loading={isSubmitting}
        variant="primary"
        size="lg"
        fullWidth
      >
        {isSubmitting ? 'Сохраняем' : isLast ? 'Готово' : 'Далее'}
      </Button>
      {submitError && (
        <Toast tone="destructive" className="text-center">
          {submitError}
        </Toast>
      )}
    </form>
  );
}
